/**
 * Where a floating assistant element (the launcher bubble, the chat window) sits — as pure
 * arithmetic. No DOM, no React: this is the part that is easy to get subtly wrong, so it is the part
 * a test can pin.
 *
 * A placed element is remembered as an EDGE ANCHOR, not a point: for each axis, which edge of the
 * placement area it measures from, and how far. The on-screen point is recomputed from the anchor
 * against the CURRENT area on every render. That is what keeps an element dragged to the right edge
 * on the right edge when the window widens — an absolute point stays at the same pixel, no longer at
 * any edge, and is stranded off-screen when the window narrows.
 */

export interface Point {
  x: number
  y: number
}

export interface Size {
  width: number
  height: number
}

/** The rectangle elements are placed in, in viewport coordinates. */
export interface Area {
  left: number
  top: number
  right: number
  bottom: number
}

export interface EdgeAnchor {
  horizontal: { edge: 'left' | 'right'; offset: number }
  vertical: { edge: 'top' | 'bottom'; offset: number }
}

/** The gap between a snapped element and its edge. The launcher's original `bottom-5 right-5`. */
export const EDGE_MARGIN_PX = 20

/**
 * Dropped within this distance of the MARGIN on the cross axis (so within margin + this of the edge,
 * 68px), a snapped element goes flush into the corner.
 */
export const CORNER_SNAP_PX = 48

/**
 * The top of the placement area: AppHeader is `h-[65px]`. Measuring from the bottom of the nav means
 * an element snapped to the "top" sits under the nav instead of covering Dashboards or the avatar.
 */
export const TOP_INSET_PX = 65

/** Where every element starts: the bottom-right corner, a margin in. */
export const DEFAULT_ANCHOR: EdgeAnchor = {
  horizontal: { edge: 'right', offset: EDGE_MARGIN_PX },
  vertical: { edge: 'bottom', offset: EDGE_MARGIN_PX },
}

export function placementArea(viewport: Size, topInset = TOP_INSET_PX): Area {
  return { left: 0, top: Math.min(topInset, viewport.height), right: viewport.width, bottom: viewport.height }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max))
}

/**
 * Keep an element fully inside the area, a margin in from every edge. An area too small for the
 * element pins it to the top-left margin rather than pushing it to a negative coordinate.
 */
export function clampToArea(point: Point, size: Size, area: Area, margin = EDGE_MARGIN_PX): Point {
  return {
    x: clamp(point.x, area.left + margin, area.right - size.width - margin),
    y: clamp(point.y, area.top + margin, area.bottom - size.height - margin),
  }
}

/**
 * The on-screen point for an anchor in the CURRENT area. Always clamped: a window that has shrunk
 * since the anchor was stored can never strand the element where nobody can reach it.
 */
export function resolveAnchor(anchor: EdgeAnchor, size: Size, area: Area, margin = EDGE_MARGIN_PX): Point {
  const { horizontal, vertical } = anchor
  const x = horizontal.edge === 'left' ? area.left + horizontal.offset : area.right - size.width - horizontal.offset
  const y = vertical.edge === 'top' ? area.top + vertical.offset : area.bottom - size.height - vertical.offset

  return clampToArea({ x, y }, size, area, margin)
}

/** The anchor that reproduces `point`: each axis measured from its NEARER edge. No snapping. */
export function anchorFromPoint(point: Point, size: Size, area: Area, margin = EDGE_MARGIN_PX): EdgeAnchor {
  const { x, y } = clampToArea(point, size, area, margin)
  const fromLeft = x - area.left
  const fromRight = area.right - (x + size.width)
  const fromTop = y - area.top
  const fromBottom = area.bottom - (y + size.height)

  return {
    horizontal: fromLeft <= fromRight ? { edge: 'left', offset: fromLeft } : { edge: 'right', offset: fromRight },
    vertical: fromTop <= fromBottom ? { edge: 'top', offset: fromTop } : { edge: 'bottom', offset: fromBottom },
  }
}

/**
 * Where an element lands when it is let go: flush against the NEAREST of the four edges.
 *
 * The axis whose nearer edge is closer snaps to the margin; a tie goes to the sides. The other axis
 * keeps its distance from its own nearer edge — unless that distance is within `cornerSnap` of the
 * margin, where it goes flush too, so the corners are easy to hit.
 */
export function snapToNearestEdge(
  point: Point,
  size: Size,
  area: Area,
  margin = EDGE_MARGIN_PX,
  cornerSnap = CORNER_SNAP_PX,
): EdgeAnchor {
  const { horizontal, vertical } = anchorFromPoint(point, size, area, margin)
  const flush = (offset: number) => (offset <= margin + cornerSnap ? margin : offset)

  if (horizontal.offset <= vertical.offset) {
    return { horizontal: { ...horizontal, offset: margin }, vertical: { ...vertical, offset: flush(vertical.offset) } }
  }

  return { horizontal: { ...horizontal, offset: flush(horizontal.offset) }, vertical: { ...vertical, offset: margin } }
}

function isAxis<E extends string>(value: unknown, a: E, b: E): value is { edge: E; offset: number } {
  if (typeof value !== 'object' || value === null) return false
  const { edge, offset } = value as { edge?: unknown; offset?: unknown }

  return (edge === a || edge === b) && typeof offset === 'number' && Number.isFinite(offset) && offset >= 0
}

/** A stored anchor, read defensively: a hand-edited, stale or foreign value is null, never a throw. */
export function parseAnchor(raw: string | null): EdgeAnchor | null {
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { horizontal, vertical } = parsed as { horizontal?: unknown; vertical?: unknown }
    if (!isAxis(horizontal, 'left', 'right') || !isAxis(vertical, 'top', 'bottom')) return null

    return {
      horizontal: { edge: horizontal.edge, offset: horizontal.offset },
      vertical: { edge: vertical.edge, offset: vertical.offset },
    }
  } catch {
    return null
  }
}
